import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import * as reportsApi from '../services/reportsApi';
import { formatDateTime } from '../utils/dateFormat';

const REPORTS = [
  { key: 'stock_analysis', label: 'Stock Analysis', path: '/reports/stock-analysis', ext: 'xlsx', description: 'Current stock, negative/low/dead lists, reorder suggestions.' },
  { key: 'order_analysis', label: 'Order Analysis', path: '/reports/order-analysis', ext: 'xlsx', description: 'Pending orders and fulfillment by customer.' },
  { key: 'dispatch_performance', label: 'Dispatch Performance', path: '/reports/dispatch-performance', ext: 'xlsx', description: 'Delayed dispatches and on-time performance by customer.' },
  { key: 'mis_summary', label: 'MIS Summary', path: '/reports/mis-summary', ext: 'xlsx', description: 'One row per design — stock, orders, dispatch, production, classification.' },
  { key: 'executive_summary', label: 'Executive Summary', path: '/reports/executive-summary', ext: 'pdf', description: 'Health score, critical issues, and top movers as a one-page PDF.' },
];

export default function ReportsPage() {
  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);

  const loadHistory = () => reportsApi.listHistory().then(setHistory);

  useEffect(() => {
    loadHistory().catch(() => {});
  }, []);

  const handleDownload = async (report) => {
    setDownloading(report.key);
    setError(null);
    try {
      const filename = `${report.key.replace('_', '-')}-${new Date().toISOString().slice(0, 10)}.${report.ext}`;
      await reportsApi.downloadReport(report.path, filename);
      await loadHistory();
    } catch (err) {
      setError(err.message || 'Could not generate the report.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      <h1>Reports Center</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        Download what the live dashboards already show as formatted files — every report reads
        the same data, so a report can never disagree with the screen it came from.
      </p>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      <div className="responsive-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        {REPORTS.map((r) => (
          <Card key={r.key} style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
              <div>
                <h3 style={{ fontSize: 'var(--font-size-base)', margin: '0 0 4px' }}>{r.label}</h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>{r.description}</p>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{r.ext}</span>
              </div>
              <Button onClick={() => handleDownload(r)} disabled={downloading === r.key}>
                {downloading === r.key ? 'Generating…' : 'Download'}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Card title="Recent Reports">
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
              {['Report', 'Format', 'Generated By', 'When'].map((h) => (
                <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.report_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', textTransform: 'capitalize' }}>{h.report_type.replace('_', ' ')}</td>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase' }}>{h.file_format}</td>
                <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{h.generated_by_name}</td>
                <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{formatDateTime(h.generated_at)}</td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>No reports generated yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}
